// Schemas and types for the docs tool, shared by server and UI.
import { z } from 'zod';

/** Word-processor formats the editor can open; everything is saved back as .docx. */
export const DOCS_SOURCE_EXTENSIONS = ['docx', 'doc', 'odt', 'rtf'] as const;
export type DocsSourceExtension = (typeof DOCS_SOURCE_EXTENSIONS)[number];

export const DOCS_EXPORT_FORMATS = ['docx', 'pdf'] as const;
export type DocsExportFormat = (typeof DOCS_EXPORT_FORMATS)[number];

export const DOCS_MIME: Record<DocsExportFormat, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
};

/** Upper bound for the editor's HTML, images included (as data URLs). */
export const DOCS_MAX_HTML_CHARS = 24 * 1024 * 1024;
/** Upper bound for one embedded image, decoded. */
export const DOCS_MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const docsImportInput = z.object({ fileId: z.uuid() });
export type DocsImportInput = z.infer<typeof docsImportInput>;

export interface DocsImportResult {
  /** Sanitized HTML ready for the editor. */
  html: string;
  name: string;
}

export const docsExportTarget = z.union([
  z.object({ fileId: z.uuid() }),
  z.object({
    name: z.string().trim().min(1).max(255),
    parentId: z.uuid().nullable().optional(),
  }),
]);
export type DocsExportTarget = z.infer<typeof docsExportTarget>;

export const docsExportInput = z.object({
  html: z.string().max(DOCS_MAX_HTML_CHARS),
  format: z.enum(DOCS_EXPORT_FORMATS),
  target: docsExportTarget,
});
export type DocsExportInput = z.infer<typeof docsExportInput>;

export function docsExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

export function isDocsSource(name: string): boolean {
  return (DOCS_SOURCE_EXTENSIONS as readonly string[]).includes(docsExtension(name));
}
